import { useNavigate } from "react-router-dom";

interface CarouselCard {
  id: number;
  title: string;
  image: string;
  tag: string;
  tagColor: string;
  meta1?: string;
  meta2?: string;
  detailId?: string; // e.g. "webinar-1"
}

interface ContentCarouselProps {
  title: string;
  icon: string;
  accentColor: string;
  cards: CarouselCard[];
  ctaLabel: string;
  onCtaClick?: (id: number) => void;
  typeName?: string;
  loading?: boolean;
}

export default function ContentCarousel({
  title,
  icon,
  accentColor,
  cards,
  ctaLabel,
  onCtaClick,
  typeName,
  loading,
}: ContentCarouselProps) {
  const navigate = useNavigate();

  return (
    <section className="py-4" dir="rtl">
      {/* Section header */}
      <div className="flex items-center justify-between px-4 mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-900"
            style={{ background: accentColor }}
          >
            <i className={`${icon} text-gray-900 text-base`}></i>
          </div>
          <h3 className="text-lg font-black text-gray-900">{title}</h3>
        </div>
        {!loading && (
          <button
            onClick={() =>
              navigate(`/all-activities${typeName ? `?type=${typeName}` : ""}`)
            }
            className="flex items-center gap-1 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            הכל <i className="ri-arrow-left-line text-sm"></i>
          </button>
        )}
      </div>

      {/* Cards scroll */}
      <div className="overflow-x-auto scrollbar-hide">
        <div className="flex gap-4 px-4 pb-2" style={{ width: "max-content" }}>
          {loading
            ? [0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-64 rounded-3xl border-[2.5px] border-gray-900 bg-white overflow-hidden flex flex-col"
                >
                  <div className="w-full h-36 bg-gray-200 animate-pulse" />
                  <div className="p-4 flex flex-col gap-2">
                    <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse" />
                    <div className="h-4 w-1/2 rounded bg-gray-200 animate-pulse" />
                    <div className="h-3 w-2/3 rounded bg-gray-100 animate-pulse" />
                    <div className="h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
                    <div className="mt-3 h-10 w-full rounded-2xl bg-gray-200 animate-pulse" />
                  </div>
                </div>
              ))
            : cards.map((card) => (
            <div
              key={card.id}
              onClick={() =>
                card.detailId && navigate(`/item/${card.detailId}`)
              }
              className="w-64 rounded-3xl border-[2.5px] border-gray-900 bg-white overflow-hidden flex flex-col transition-transform hover:-translate-y-1 cursor-pointer"
            >
              {/* Image */}

              <div className="relative w-full h-36 overflow-hidden bg-gray-100">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                {/* Tag */}
                <div
                  className="absolute top-3 right-3 flex items-center gap-1 border-2 border-gray-900 rounded-full px-2.5 py-1"
                  style={{ background: card.tagColor }}
                >
                  <span className="text-xs font-black text-gray-900">
                    {card.tag}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col gap-2 flex-1">
                <h4 className="text-sm font-black text-gray-900 leading-snug line-clamp-2">
                  {card.title}
                </h4>

                <div className="flex flex-col gap-1 mt-auto">
                  {card.meta1 && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-calendar-line text-gray-400"></i>
                      <span>{card.meta1}</span>
                    </div>
                  )}
                  {card.meta2 && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-map-pin-line text-gray-400"></i>
                      <span>{card.meta2}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCtaClick?.(card.id);
                  }}
                  className="mt-3 w-full rounded-2xl border-2 border-gray-900 py-2.5 text-sm font-black text-gray-900 transition-all hover:opacity-80 whitespace-nowrap cursor-pointer"
                  style={{ background: accentColor }}
                >
                  {ctaLabel}
                </button>
              </div>
            </div>
            ))}
        </div>
      </div>
    </section>
  );
}
